import * as React from 'react';
import ConfirmDialog from 'components/ConfirmDialog';
import { confirmDialogState, claimConfirmDialogRendering, releaseConfirmDialogRendering } from 'state/confirm-dialog';

/**
 * Global ConfirmDialog component that renders based on the confirm-dialog state.
 * This should be rendered once at the app root level.
 *
 * @example
 * ```tsx
 * import GlobalConfirmDialog from 'components/ConfirmDialog/GlobalConfirmDialog';
 *
 * function App() {
 *   return (
 *     <>
 *       <YourAppContent />
 *       <GlobalConfirmDialog />
 *     </>
 *   );
 * }
 * ```
 */
export interface IGlobalConfirmDialogProps {
  /**
   * Optional callback that returns whether the host web component is visible.
   * When multiple web component roots share the same dialog state, a hidden
   * host (e.g. `<e3d-sharing-dashboard hidden>`) should NOT claim rendering
   * because the dialog, even with `position: fixed`, is not interactive
   * inside a hidden shadow root.
   */
  isHostVisible?: () => boolean;
  /** Lowercase tag name of this instance's host web component (e.g. 'e3d-stream'), used to match against preferredClaimant. */
  hostTagName?: string;
  /**
   * When set, this instance waits this many milliseconds before attempting its claim, and ignores
   * `preferredClaimant` when it finally does (AVS-4404). It is how a last-resort host defers to
   * every integrator-placed host: those claim synchronously, so anything still unclaimed after the
   * delay is a dialog nobody could show. The preferred claimant already had its chance by then, and
   * a dialog rendered in a less-preferred host beats one that hangs unanswered.
   *
   * Leave undefined (the default) for integrator-placed hosts — they claim synchronously.
   *
   * `0` behaves identically to `undefined`: both claim synchronously and never escalate to the
   * delayed, ignore-preferredClaimant last-resort claim below.
   */
  claimDelayMs?: number;
}

export default function GlobalConfirmDialog({
  isHostVisible,
  hostTagName,
  claimDelayMs,
}: IGlobalConfirmDialogProps = {}) {
  const dialogState = confirmDialogState.useState();
  // Track whether THIS instance (not any instance) has claimed rendering rights.
  // Multiple web component roots share the same confirmDialogState but only one
  // should render at a time. useEffect (not useMemo) is used here because
  // claimConfirmDialogRendering() is a side effect that mutates shared state.
  const [canRender, setCanRender] = React.useState(false);

  React.useEffect(() => {
    if (!dialogState.isOpen) {
      setCanRender(false);
      return;
    }
    if (dialogState.isBeingRendered) return;

    // claimConfirmDialogRendering() re-reads live state, so a stale closure cannot double-claim.
    const tryClaim = (ignorePreferredClaimant: boolean) => {
      // Only claim if no preferredClaimant is set or this instance's hostTagName matches (AVS-4347)
      const isPreferredClaimant =
        ignorePreferredClaimant || !dialogState.preferredClaimant || dialogState.preferredClaimant === hostTagName;
      if (isPreferredClaimant && (!isHostVisible || isHostVisible()) && claimConfirmDialogRendering()) {
        setCanRender(true);
      }
    };

    if (!claimDelayMs) {
      tryClaim(false);
      return;
    }

    // Deferred last-resort claim (AVS-4404). If any other host claims in the meantime,
    // isBeingRendered flips, this effect re-runs, and the cleanup below cancels the timer.
    const claimTimer = setTimeout(() => tryClaim(true), claimDelayMs);
    return () => clearTimeout(claimTimer);
  }, [
    dialogState.isOpen,
    dialogState.isBeingRendered,
    // dialogId, so a stale-dialog reset gets a fresh claim attempt. showConfirmDialog() resets a
    // never-rendered dialog by cancelling it and opening the new one in the same tick, and React
    // batches both setValues into one render: isOpen never observably flips. Without dialogId in
    // the deps, a pending last-resort timer armed for the old dialog survives into the new one and
    // fires with ignorePreferredClaimant, and a synchronous host never retries at all.
    dialogState.dialogId,
    dialogState.preferredClaimant,
    isHostVisible,
    hostTagName,
    claimDelayMs,
  ]);

  // A host can become invisible WITHOUT unmounting: DCC sets `display: none` on an ancestor of
  // <e3d-sharing-dashboard> while sharing starts. The claim effect above only checks visibility at
  // claim time, so the dialog stays claimed by a host that can never paint it, and a claimed dialog
  // has no settle timer — share() hangs forever. Releasing the claim lets the runtime-owned
  // last-resort host (which is never hidden) pick the dialog up on its next 250 ms attempt.
  //
  // Observing document.body rather than the host element (unlike the toast observer in
  // WebComponentRoot) because checkVisibility() also fails on a hidden ANCESTOR, and that ancestor
  // is never the host element itself. `childList` is deliberately absent: wc() unmounts its React
  // root on detach (utils/web-component.tsx), so a removed host already releases via the cleanup
  // below. ConfirmDialog does not portal to document.body, so its own render cannot retrigger this.
  //
  // Known limitation: a host hidden for a single frame (an integrator re-render toggling a class)
  // hands the dialog to the last-resort host permanently — nothing re-offers it once the preferred
  // host is visible again, because its claim effect's dependencies never changed. A dialog that
  // renders in a less-preferred host still resolves, which is worth more than the hang it replaces.
  React.useEffect(() => {
    if (!canRender || !isHostVisible) return;
    if (typeof document === 'undefined' || !document.body) return;

    const releaseIfHidden = () => {
      if (isHostVisible()) return;
      releaseConfirmDialogRendering();
      setCanRender(false);
    };

    const visibilityObserver = new MutationObserver(releaseIfHidden);
    visibilityObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden'],
      subtree: true,
    });
    return () => visibilityObserver.disconnect();
  }, [canRender, isHostVisible]);

  // If THIS instance is holding the rendering claim when it unmounts (e.g. an integrator
  // removes its host element in reaction to sharing-started, AVS-4351), release the claim
  // instead of leaving isBeingRendered stuck true forever — that would block both another
  // visible host from claiming it and the unclaimed-dialog settle timeout from ever firing,
  // since that timeout only settles a dialog nobody claimed, not one whose claimant vanished.
  const canRenderRef = React.useRef(canRender);
  canRenderRef.current = canRender;
  React.useEffect(() => {
    return () => {
      if (canRenderRef.current) {
        releaseConfirmDialogRendering();
      }
    };
  }, []);

  if (!canRender || !dialogState.isOpen) {
    return null;
  }

  return (
    <ConfirmDialog
      isOpen={dialogState.isOpen}
      title={dialogState.title}
      message={dialogState.message}
      confirmText={dialogState.confirmText}
      cancelText={dialogState.cancelText}
      hideCancel={dialogState.hideCancel}
      onConfirm={dialogState.onConfirm}
      onCancel={dialogState.onCancel}
      data-testid={dialogState['data-testid']}
    >
      {dialogState.children}
    </ConfirmDialog>
  );
}
